import { useContext, useEffect } from "react"

import { StopOutlined } from "@ant-design/icons"
import accounting, { formatNumber } from "accounting"
import { Typography } from "antd"
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import localizedFormat from "dayjs/plugin/localizedFormat"
import utc from "dayjs/plugin/utc"
import { createBrowserHistory } from "history"
import { t } from "i18next"
import { UNSAFE_NavigationContext } from "react-router-dom"

import { filtersTypeById, filtersTypeByIdMap } from "../Components/Filters/filtersTypeById"

const StopIcon = <StopOutlined style={{ transform: "rotate(90deg)", fontSize: 16 }} />

const { Text } = Typography

dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(localizedFormat)

export function getCookie(name) {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(";").shift()
  return null
}

export const dateFormat = "DD MMM YYYY"

export const onboardingStepPath = {
  2: "/getting_started",
  3: "/onboarding/business-info",
  4: "/onboarding/payment-connect",
  5: "/onboarding/enrollment",
  6: "/onboarding/alerts",
  7: "/onboarding/card-info",
  8: "/onboarding/top-up"
}

export const createAccountStepPath = {
  1: "/create-account/business-info",
  2: "/create-account/payment-connect",
  3: "/create-account/enrollment",
  4: "/create-account/alerts",
  5: "/create-account/card-info",
  6: "/create-account/top-up",
  7: "/getting_started/new-business"
}

const ISO_DATE = "YYYY-MM-DD"
const toIsoDate = (d) => (dayjs.isDayjs(d) ? d.format(ISO_DATE) : d)

const resolvePeriod = (value) => {
  if (Array.isArray(value)) {
    const [from, to] = value
    return [toIsoDate(from), toIsoDate(to)]
  }

  const period = value?.period

  if (value?.type === "custom") return Array.isArray(period) ? period : []
  if (value === "all") return []

  const [quantity, unit] = value?.split(" ") ?? [1, "days"]
  return [dayjs().subtract(+quantity, unit).format(ISO_DATE), dayjs().format(ISO_DATE)]
}

export const getDates = (value) => {
  const [start_date, end_date] = resolvePeriod(value)
  return { start_date, end_date }
}

export const manageEnrollmentStepPath = (enrollmentId, isAdmin) => ({
  1: isAdmin
    ? `/business_settings/enrollment/${enrollmentId}/payment-connect`
    : `/configure/enrollment/${enrollmentId}/payment-connect`,
  2: isAdmin
    ? `/business_settings/enrollment/${enrollmentId}/alerts`
    : `/configure/enrollment/${enrollmentId}/alerts`
})

export const excludeReferences = [
  ...Object.values(createAccountStepPath),
  ...Object.values(onboardingStepPath)
]

export const validateUrl =
  /^(http|ftp|https)?(:\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^!=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])+$/im

export function ensureHttps(url) {
  if (!/^https?:\/\//i.test(url)) {
    return "https://" + url
  }
  return url
}

// Resolve a (possibly relative) docs URL against the API host, not the app origin.
export const buildPublicApiDocsUrl = (url) => {
  let apiOrigin
  try {
    apiOrigin = new URL(process.env.REACT_APP_BASE_API_ROUTE).origin
  } catch (e) {
    apiOrigin = window.location.origin
  }

  const defaultPath = "/api/public/docs/"

  if (!url) {
    return `${apiOrigin}${defaultPath}`
  }

  if (/^https?:\/\//i.test(url)) {
    return url
  }

  return `${apiOrigin}${url.startsWith("/") ? "" : "/"}${url}`
}

export const getBase64 = (img, callback) => {
  const reader = new FileReader()
  reader.addEventListener("load", () => callback(reader.result))
  reader.readAsDataURL(img)
}

export const remainingTime = (alertTimestamp, alertType, proofDeadline, errorMessage) => {
  const currentTime = dayjs.utc()

  // If proofDeadline is specified, calculate the remaining time until it
  if (proofDeadline) {
    const deadline = dayjs.utc(proofDeadline);
    const hoursRemaining = deadline.diff(currentTime, "hour");
    const diffMs = deadline.diff(currentTime);

    if (diffMs <= 0) return ''

    if (hoursRemaining < 1) {
      const minsRemaining = Math.ceil(diffMs / 60000)
      if (minsRemaining >= 60) return "1h"
      return `${Math.max(1, minsRemaining)}min`
    } else if (hoursRemaining < 100) {
      return `${hoursRemaining}h`;
    } else {
      const daysRemaining = Math.floor(hoursRemaining / 24)
      return `${daysRemaining}d`
    }
  }

  const alertTime = dayjs.utc(alertTimestamp)

  const isShortenedDeadline =
    errorMessage?.includes("The alert amount exceeds your set threshold in Business settings") ||
    errorMessage?.includes("Transaction not found in the payment processor")

  // Define the time thresholds for each alert type, in minutes
  const timeThresholds = {
    rdr: 0,
    cdrn: isShortenedDeadline ? 71 * 60 + 55 : 72 * 60, // minutes
    ethoca: isShortenedDeadline ? 23 * 60 + 55 : 24 * 60, // minutes
    "requires proof": 24 * 14 * 60 // minutes
  }

  // Calculate the time difference in minutes
  const timeDifference = currentTime.diff(alertTime, "minute")
  // Get the threshold for the given alert type
  const threshold = timeThresholds[alertType]

  // Calculate remaining time until status change
  const remainingMinutes = threshold - timeDifference
  const remainingHours = Math.trunc(remainingMinutes / 60)
  const remainingMs = threshold * 60000 - currentTime.diff(alertTime)

  if (remainingMs <= 0) return ''

  if (remainingHours < 1) {
    const remainingMins = Math.ceil(remainingMs / 60000)
    if (remainingMins >= 60) return "1h"
    return `${Math.max(1, remainingMins)}min`
  } else if (remainingHours < 100) {
    return `${remainingHours}h`
  } else {
    const remainingDays = Math.floor(remainingHours / 24)
    return `${remainingDays}d`
  }
}

export const tagMap = {
  draft: "default",
  active: "success",
  auto_refunded: "success",
  "auto-refunded": "success",
  "auto refunded": "success",
  manual_refund: "success",
  manually_refunded: "success",
  "manually refunded": "success",
  resolved: "success",
  request_sent: "warning",
  "request sent": "warning",
  onboarded: "success",
  updated: "success",
  pending: "processing",
  warning: "warning",
  in_progress: "warning",
  "in progress": "warning",
  processing: "warning",
  attention: "warning",
  lost: "error",
  successful: "success",
  declined: "error",
  failed: "error",
  cancelled: "error",
  chargeback: "error",
  blocked: "error",
  inactive: "error",
  incomplete: "warning",
  connected: "success",
  paid: "success",
  unpaid: "error",
  canceled: "default",
  needs_help: "error",
  "needs help": "error",
  wrong_descriptor: "error",
  "wrong descriptor": "error",
  enrolled_elsewhere: "error",
  "enrolled elsewhere": "error",
  error: "error",
  invited: "warning",
  invite_sent: "warning",
  unverified: "default",
  setup_pending: "warning",
  "setup pending": "warning",
  expired: "error",
  approved: "success",
  rejected: "error",
  "requires proof": "warning",
  "auto approved": "success",
  unenrolled: "error",
  success: "success",
  receipt: "processing",
  succeeded: "success",
  pending_unenroll: "warning",
  in_re_enrollment: "processing",
  generic_descriptor: "error",
  banned_descriptor: "error",
  invalid_descriptor: "error",
  verification_needed: "warning",
  invalid_arns: "error",
  needs_assistance: "error",
  credited: "success"
}

export const creditRequestOptions = [
  {
    value: "Transaction not found",
    label: "Transaction not found"
  },
  {
    value: "Transaction was refunded before the alert",
    label: "Transaction was refunded before the alert"
  },
  {
    value: "Transaction became a chargeback before the alert",
    label: "Transaction became a chargeback before the alert"
  },
  {
    value: "Alert went to failed/not authenticated transaction",
    label: "Alert went to failed/not authenticated transaction"
  },
  {
    value: "Other",
    label: "Other"
  }
]

export const faqIcons = {
  "Join the program": "join_referral_program",
  "Share link or email": "referral_link",
  "Get rewarded in real-time": "megaphone",
  "Earn cash for every alert": "currency",
  "Milestone bonuses": "milestone_bonuses",
  "Ongoing earnings for life": "earnings_for_life",
  "Sticky customers": "customers",
  "Lower your own alert costs": "lower_alert_cost",
  "Fast & easy referral process": "zap"
}

export const tagColorMap = {
  pending: "#661AC6",
  Pending: "#661AC6",
  in_progress: "#661AC6",
  "In progress": "#661AC6",
  "IN PROGRESS": "#661AC6",
  verified: "#661AC6",
  Verified: "#661AC6",
  failed: "#EF4243",
  "Failed transaction": "#EF4243",
  "failed transaction": "#EF4243",
  "Previously refunded": "#34C759",
  "previously refunded": "#34C759",
  "Already chargebacked": "#440F0E",
  "Already a chargeback": "#440F0E",
  Duplicate: "#D0D0D4",
  duplicate: "#D0D0D4",
  needs_help: "#EF4243"
}

export const statusTextMap = {
  auto_refunded: "Auto-refunded",
  "Auto refunded": "Auto-refunded",
  "Auto-refunded": "Auto-refunded",
  manual_refund: "Manual refund",
  manually_refunded: "Manually refunded",
  "Manually refunded": "Manually refunded",
  in_progress: "In progress",
  request_sent: "Request sent",
  onboarded: "Onboarded",
  pending: "Pending",
  active: "Active",
  failed: "Failed",
  blocked: "Blocked",
  inactive: "Inactive",
  declined: "Declined",
  incomplete: "Incomplete",
  updated: "Updated",
  paid: "Paid",
  unpaid: "Unpaid",
  canceled: "Canceled",
  cancelled: "Cancelled",
  expired: "Expired",
  needs_help: "Needs help",
  wrong_descriptor: "Wrong descriptor",
  enrolled_elsewhere: "Enrolled elsewhere",
  unverified: "Unverified",
  verified: "Verified",
  setup_pending: "Setup pending",
  invited: "Invited",
  invite_sent: "Invite sent",
  "no role status": "No role status",
  "no ba": "No BA",
  "Failed transaction": "Failed transaction",
  "Failed-transaction": "Failed transaction",
  failed_transaction: "Failed transaction",
  "Previously refunded": "Previously refunded",
  Duplicate: "Duplicate",
  duplicate: "Duplicate",
  unenrolled: "Unenrolled",
  "Already a chargeback": "Already a chargeback",
  generic_descriptor: "Generic descriptor",
  banned_descriptor: "Banned descriptor",
  invalid_arns: "Invalid ARN/BIN/CAID",
  invalid_descriptor: "Invalid descriptor",
  needs_assistance: "Needs assistance",
  verification_needed: "Verification needed",
  pending_unenroll: "Pending unenroll",
  in_re_enrollment: "In re-enrollment",
  next: "Next Commerce",
  succeeded: "Succeeded"
}

export const excludeOption = ["needs_assistance"]

export const enrollmentsStatus = {
  rdr: "Recommended",
  cdrn: "Optional",
  ethoca: "Recommended"
}

export const enrollmentStatusMap = {
  pending: {
    text: "Pending",
    altText: "Pending",
    icon: "loader",
    color: "#F6B60E"
  },
  active: {
    text: "Active",
    altText: "Active",
    icon: "check_mark",
    color: "#16895A"
  },
  enable: {
    text: "Enable",
    altText: "Enable",
    icon: "circle_placeholder_dashed",
    color: "#225AEA"
  },
  unenrolled: { text: "Unenrolled", icon: StopIcon, color: "#D71313" },
  failed: { text: "Failed", icon: StopIcon, color: "#D71313" },
  needs_help: { text: "Needs help", icon: "warning", color: "#D71313" }
}

export const tooltipTextMap = {
  in_progress: "Enrollment is ongoing but not finalized.",
  pending: "The descriptor is waiting for approval or further processing.",
  active: "The descriptor is successfully enrolled.",
  unenrolled: "The descriptor has been removed from the enrollment.",
  pending_unenroll: "Chargeback is waiting for unenrollment.",
  in_re_enrollment: "Chargeback is processing your enrollment.",
  enrolled_elsewhere: "The descriptor is already in use elsewhere.",
  generic_descriptor: "The descriptor is too broad or non-specific.",
  banned_descriptor: "The descriptor is not allowed due to policy restrictions.",
  invalid_arns: "One or more ARN, BIN, or CAID codes are not correct.",
  invalid_descriptor: "The descriptor is not usable or has been flagged as incorrect.",
  needs_assistance: "ARN codes are required to complete enrollment.",
  verification_needed: "Manual verification is needed before activation.",
  needs_help: "error"
}

export const cdrnModalList = [
  "CDRN cases are pulled from RDR",
  "No added coverage when using both",
  "CDRN provides more manual, US-only alerts"
]

export const alertsTooltipStatusText = {
  "manually refunded":
    'If the transaction was manually refunded in the payment processor, update the status to "Manually Refunded" to inform the issuer that the dispute has been resolved.',
  declined:
    'If you do not intend to refund the transaction and prevent this transaction from becoming a chargeback, set the status to "Declined".',
  "not found":
    'If you have searched for the transaction across all payment processors and could not locate it, set the status to "Not Found".',
  "previously refunded":
    'If you manually found the transaction in the payment processor and it was refunded before the alert was received, set the status to "Previously refunded".',
  "failed transaction":
    'If the transaction was found to be failed or not authorized in the payment processor, set the status to "Failed transaction".',
  "already a chargeback":
    'If the transaction had already become a chargeback before the alert was received, set the status to "Already a chargeback".'
}

export const statusMap = {
  pending: "pending",
  in_progress: "progress",
  success: "success",
  active: "success",
  error: "error",
  failed: "error",
  unenrolled: "error",
  enrolled_elsewhere: "error",
  generic_descriptor: "error",
  banned_descriptor: "error",
  invalid_arns: "error",
  invalid_descriptor: "error",
  needs_assistance: "error",
  verification_needed: "verification_needed",
  pending_unenroll: "pending",
  in_re_enrollment: "pending",
  needs_help: "error"
}

//"unverified", "verified", "setup_pending", "active", "invited"

export const paymentProcessorMap = {
  paypal: "PayPal",
  shopify: "Shopify",
  shopify_payments: "Shopify",
  stripe: "Stripe",
  stripe_dev: "Stripe DEV",
  chargebee: "Chargebee",
  recurly: "Recurly",
  braintree: "Braintree",
  manual_refunds: "Manual Refunds",
  premium_support: "Premium support",
  full: "Full",
  slack: "Slack notifications",
  "authorize.net": "Authorize.net",
  nmi: "NMI",
  airwallex: "Airwallex",
  next: "Next Commerce",
  adyen: "Adyen",
  maverick: "Maverick Payments"
}

export const handleViewTutorial = () => {
  openHideLink("https://help.chargeback.io/en/articles/13457478-adyen-integration-guide", "_blank")
}

export const paymentProcessorTitle = {
  ...paymentProcessorMap,
  "authorize.net": "Authorize.net"
}

export const paymentProcessorDescription = {
  slack: "Connect your Slack workspace to receive notifications for all incoming alerts."
}

export const notificationConfig = {
  closeIcon: <Text>{t("Dismiss")}</Text>,
  style: { minWidth: "max-content" },
  duration: 60
}

export const referralsSubtitle = {
  Invite:
    "Refer friends to Chargeback.io by link or email and earn for every Alert they get. Additionally, you receive bonuses at key Alert milestones.",
  Earnings:
    "Your personalized dashboard to track earnings, bonuses, and all details of the businesses you’ve referred."
}

export const serviceMap = {
  ethoca: "Ethoca",
  cdrn: "CDRN",
  rdr: "RDR"
}

export const serviceImgTooltip = {
  dark: {
    // ethoca: 'mastercard_logo_dark',
    // rdr: 'verifi_logo_dark',
    // cdrn: 'verifi_logo_dark',
    ethoca: "mastercard",
    rdr: "verifi",
    cdrn: "verifi"
  },
  light: {
    // ethoca: 'mastercard_logo',
    // rdr: 'verifi_logo',
    // cdrn: 'verifi_logo',
    ethoca: "mastercard",
    rdr: "verifi",
    cdrn: "verifi"
  }
}

export const onboardingServiceImgGray = {
  dark: {
    ethoca: "mastercard_icon",
    rdr: "visa_logo",
    cdrn: "visa_logo"
  },
  light: {
    ethoca: "mastercard_icon",
    rdr: "visa_logo",
    cdrn: "visa_logo"
  }
}

export const serviceImgGray = {
  dark: {
    ethoca: "mastercard_dark",
    rdr: "verifi_dark",
    cdrn: "verifi_dark"
  },
  light: {
    ethoca: "mastercard_gray",
    rdr: "verifi_gray",
    cdrn: "verifi_gray"
  }
}

export const serviceImg = {
  dark: {
    ethoca: "mastercard",
    rdr: "visa",
    cdrn: "visa"
  },
  light: {
    ethoca: "mastercard",
    rdr: "visa",
    cdrn: "visa"
  }
}

export const serviceLegend = {
  dark: {
    ethoca: "mastercard_dark",
    rdr: "verifi_dark",
    cdrn: "verifi_dark"
  },
  light: {
    ethoca: "mastercard",
    rdr: "verifi_gray",
    cdrn: "verifi_gray"
  }
}

export const alertsColumnIconMap = {
  id: "hashtag",
  alert_type: "user_square",
  alert_reason: "user_square",
  alert_status: "pulse",
  transaction_timestamp: "calendar_days",
  request_date_timestamp: "calendar_days",
  alert_timestamp: "calendar_days",
  alert_service: "card",
  country: "globus",
  amount: "money",
  request_amount: "money",
  merchant_descriptor: "square_lines",
  bank_name: "bank",
  card_last4: "card",
  payment_processor: "processor",
  payment_system: "processor",
  alert_id: "hashtag",
  transaction_id: "hashtag",
  demo_mode: "user_circle",
  mode: "user_circle",
  status_set: "settings",
  credit_request_type: "money",

  //admin part
  steps_finished: "check_mark",
  sequence_number: "hashtag",
  status: "pulse",
  request_status: "pulse",
  user_full_name: "user",
  customer_full_name: "user",
  user_email: "email",
  customer_email: "email",
  business_account: "business",
  business_url: "link",
  alerts_count: "notifications",
  paid_alerts_count: "notifications",
  alerts_in_debt_count: "notifications",
  billing_descriptors: "square_lines",
  arn_codes: "square_lines", //for requestPage Table
  bin_codes: "square_lines", //for requestPage Table
  caid_codes: "square_lines", //for requestPage Table
  enrollments: "piggy_bank",
  connected_payment_processors: "processor",
  created_timestamp: "calendar_days",
  updated_timestamp: "calendar_days",
  update_date_timestamp: "calendar_days",
  enable_auto_refunds: "money_hand",
  auto_refund_amount_other: "money",
  auto_refund_amount_rdr: "money",
  user_card: "card",
  balance_before: "money",
  balance_after: "money",
  balance_current: "money",
  balance_usage: "check_mark",
  updated_stripe_perm: "check_mark",
  request_type: "question_help",
  request_reason: "question_help",
  rejection_reason: "triangle_exclamation",
  current_amount: "money_hand",
  requested_amount: "money",
  integration_url: "list_sparkle",
  description: "square_lines",
  is_use_another_solutions: "bell",
  another_solution_name: "square_info",
  previously_connected_enrollments: "piggy_bank_bold",
  is_user_notified_previous_solution: "message",
  media_source: "receipt_bill",
  full_name: "user",
  email: "email",
  business_name: "business",
  alerts_period: "calendar_days",
  period: "calendar_days",
  invoice_date: "calendar_days",
  date: "calendar_days",
  invoice_amount: "money",
  shopify_invoice_id: "hashtag",
  invoice_id: "hashtag",
  is_reissued: "arrow_rotate",
  stripe_charged: "stripe_charged",
  charge_by: "charge_by",
  pricing_schema: "money",
  type: "question_help",
  applied: "check",
  loyal_billing: "money2",

  ethoca_price: "mastercard",
  rdr_1_price: "verifi",
  cdrn_price: "verifi",

  count_of_free_alerts: "notifications",

  customer_unsubscribed: "off",
  auto_cancel_subscriptions: "off",
  auto_cancel_amount: "money",

  user_visibility: "eye",

  arn: "hashtag", //for alert Table
  bin: "hashtag", //for alert Table
  caid: "hashtag", //for alert Table
  auth_code: "hashtag",

  invoice_link: "link",
  last_active: "calendar_days",
  ba_status: "pulse",
  role: "users",
  role_status: "pulse",
  paid_status: "pulse",
  provider_status: "pulse",
  provider_reason: "square_info",
  attachments: "paperclip",
  admin_comment: "messages",
  admin_screenshots: "camera",
  source_id: "hashtag",
  balance_transaction_timestamp: "calendar_days",
  source_type: "navigation",
  subtype: "focus",
  paid_with: "money2",
  visa_arn_code_date: "calendar_days",
  sum: "currency_gray",
  admin_name: "user_circle2",
  customer_full_name: "user_circle2",
  categories: "users",
  visits_per_month: "bar_chart",
  visits_updated: "calendar_days",
  topup_type: "focus",
  created: "calendar_days",
  updated: "calendar_days",
  ba_name: "business",
  user_name: "user",
  original_alert_id: "hashtag",
  billing_descriptor_status: "pulse"
}

export const creditsColumnIconMap = {
  ...alertsColumnIconMap,
  request_status: "heart_beat",
  request_type: "contrast"
}

export const usersColumnIconMap = {
  ...alertsColumnIconMap,
  locked: "lock",
  last_lock_at: "calendar_days"
}

export const adminUsersColumnIconMap = {
  ...alertsColumnIconMap,
  admin_full_name: "user",
  email: "email",
  role: "user_circle_2",
  invite_sent: "calendar_days",
  last_2fa_access: "calendar_days",
  last_activity: "calendar_days"
}

export const perksAdminColumnIconMap = {
  row_number: "hashtag",
  is_visible: "eye",
  created: "calendar_days",
  partner_name: "business",
  partner_website: "link",
  offer_details: "square_lines",
  description: "square_lines",
  url: "link",
  promocode: "hashtag",
  comment: "messages"
}

export const configureDate = (selectPeriod, customPeriod) => {
  const data = {}
  if (selectPeriod === "all" || selectPeriod === "custom") {
    if (selectPeriod === "custom") {
      if (customPeriod?.[0] && customPeriod?.[1]) {
        data["created_timestamp"] = customPeriod
      } else {
        return
      }
    }
  } else {
    const [quantity, val] = selectPeriod?.split(" ") || [1, "days"]
    const max = dayjs().format("YYYY-MM-DD")
    const min = dayjs().subtract(+quantity, val).format("YYYY-MM-DD")
    data["created_timestamp"] = [min, max]
  }
  return data
}

export const getCalculatedPeriod = (period) => {
  if (period === "all") return null
  if (period?.type === "custom") return period.period
  if (Array.isArray(period)) {
    const [from, to] = period
    if (!from || !to) return null
    return [dayjs.isDayjs(from) ? from : dayjs(from), dayjs.isDayjs(to) ? to : dayjs(to)]
  }
  const [quantity, val] = period?.split(" ") || [1, "days"]
  const max = dayjs()
  const min = dayjs().subtract(+quantity, val)
  return [min, max]
}

export const buildFormData = (data, fileKey) => {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (key !== fileKey && value) {
      formData.append(key, value)
    }
  })

  if (data[`${fileKey}`]) {
    data[`${fileKey}`].forEach((file, id) => {
      const fileObj = file.originFileObj ? file.originFileObj : file
      formData.append(`file_${id + 1}`, fileObj)
    })
  }

  return formData
}

export const formDataHeaders = { "Content-Type": "multipart/form-data" }

export const defaultFiltersByFolders = {
  enrollments: {
    null: { request_status: "all", is_archived: null, is_unassigned: null },
    new: { request_status: "all", is_archived: null, is_unassigned: null },
    pending: {
      request_status: ["pending", "in_re_enrollment"],
      is_archived: "false",
      is_unassigned: "false"
    },
    needs_assistance: {
      request_status: ["needs_assistance"],
      is_archived: "false",
      is_unassigned: "false"
    },
    in_progress: {
      request_status: ["in_progress", "verification_needed"],
      is_archived: "false",
      is_unassigned: "false"
    },
    active: {
      request_status: ["active"],
      is_archived: "false",
      is_unassigned: "false"
    },
    failed: {
      request_status: [
        "enrolled_elsewhere",
        "generic_descriptor",
        "banned_descriptor",
        "invalid_descriptor",
        "invalid_arns"
      ],
      is_unassigned: "false",
      is_archived: "false"
    },
    unenrolled: {
      request_status: ["unenrolled"],
      is_archived: "false",
      is_unassigned: "false"
    },
    pending_unenroll: {
      request_status: ["pending_unenroll"],
      is_archived: "false",
      is_unassigned: "false"
    },
    unassigned: {
      request_status: ["pending", "active", "unenrolled"],
      is_archived: "false",
      is_unassigned: "true"
    },
    archive: {
      request_status: "all",
      is_archived: "true",
      is_unassigned: null
    }
  },
  descriptors: {
    null: { request_status: "all", is_archived: null },
    new: { request_status: ["pending", "needs_help"], is_archived: "false" },
    processed: {
      request_status: ["request_sent", "active"],
      is_archived: "false"
    },
    archive: { request_status: "all", is_archived: "true" },
    need_communication: {
      request_status: ["wrong_descriptor", "Enrolled Elsewhere", "Missing Enrollment Info"],
      is_archived: "false"
    }
  },
  auto_refund: {
    null: { request_status: "all", is_archived: null },
    new: { request_status: ["pending"], is_archived: "false" },
    processed: {
      request_status: ["request_sent", "updated"],
      is_archived: "false"
    },
    archive: { request_status: "all", is_archived: "true" }
  },
  credits: {
    null: { provider_status: "all", is_archived: null },
    pending: { provider_status: ["Pending"], is_archived: "false" },
    processed: { provider_status: ["Request sent"], is_archived: "false" },
    completed: {
      provider_status: ["Approved", "Rejected", "Ineligible"],
      is_archived: "false"
    },
    awaiting_proof: {
      provider_status: ["Requires proof"],
      is_archived: "false"
    },
    archive: { provider_status: "all", is_archived: "true" }
  }
}

export const serializeData = ({ exclude_fields, ...data } = {}, separator, withEmpty) => {
  const numberFilterTypeMap = {
    is_equal_to: "=",
    is_between: "",
    is_greater_than: ">",
    is_less_than: "<"
  }

  const trueFalseFields = [
    "enable_auto_refunds",
    "is_use_another_solutions",
    "is_user_notified_previous_solution",
    "is_reissued",
    "applied",
    "customer_unsubscribed",
    "auto_cancel_subscriptions",
    "user_visibility",
    "balance_usage",
    "admin_screenshots",
    "stripe_charged",
    "demo_mode",
    "updated_stripe_perm",
    "locked",
    "billable"
  ]

  const noneFieldsMap = {
    role: "no role",
    role_status: "no role status",
    ba_status: "no ba status"
  }
  const result = {}
  const isNumberFilterField = (fieldKey) => {
    if (filtersTypeById?.[fieldKey]?.type === "number") return true

    return Object.values(filtersTypeByIdMap || {}).some(
      (group) => group?.[fieldKey]?.type === "number"
    )
  }

  for (const key in data) {
    if (key.startsWith("__")) continue
    const fieldValue = data[key]
    const finalKey = separator
      ? key.split(separator)?.[1] || key
      : exclude_fields?.length && exclude_fields?.includes(key)
        ? `exclude_${key}`
        : key

    if (finalKey === "role") {
      if (Array.isArray(fieldValue)) {
        if (fieldValue.includes("all")) {
          continue
        }
        if (fieldValue.includes("No role")) {
          result["role_none"] = "none"
        }
        const transformedValues = fieldValue
          .filter((value) => value !== "No role")
          .map((value) => (value.toLowerCase() === "view only" ? "view_only" : value))
        if (transformedValues.length > 0) {
          result[finalKey] = transformedValues.join(",")
        }
      } else if (typeof fieldValue === "string") {
        if (fieldValue === "all") {
          continue
        }
        if (fieldValue === "No role") {
          result["role_none"] = "none"
        } else {
          result[finalKey] = fieldValue.toLowerCase() === "view only" ? "view_only" : fieldValue
        }
      }
      continue
    }

    if (finalKey === "role_status") {
      if (Array.isArray(fieldValue)) {
        if (fieldValue.includes("all")) {
          continue
        }
        if (fieldValue.includes("No role status")) {
          result["role_status_none"] = "none"
        }
        const filteredValues = fieldValue.filter((value) => value !== "No role status")
        if (filteredValues.length > 0) {
          result["role_status"] = filteredValues.join(",")
        }
      } else if (typeof fieldValue === "string") {
        if (fieldValue.includes("all")) {
          continue
        }
        const valuesArray = fieldValue.split(",").map((v) => v.trim())
        if (valuesArray.includes("No role status")) {
          result["role_status_none"] = "none"
        }

        const filteredValues = valuesArray.filter((value) => value !== "No role status")
        if (filteredValues.length > 0) {
          result["role_status"] = filteredValues.join(",")
        }
      }
      continue
    }

    if (Array.isArray(fieldValue) && fieldValue.length) {
      if (
        finalKey?.includes("_timestamp") ||
        [
          "alerts_period",
          "invoice_date",
          "last_active",
          "date",
          "visa_arn_code_date",
          "business_account_created"
        ].includes(finalKey)
      ) {
        //result[finalKey] = fieldValue.map(date => dayjs(date).format('YYYY-MM-DDTHH:mm:ss[Z]')).join(',')
        result[finalKey] = fieldValue
          .slice(0, 2)
          .filter((date) => date && dayjs(date, "YYYY-MM-DD", true).isValid())
          .map((date, index) => date + (index === 0 ? "T00:00:00Z" : "T23:59:59Z"))
          .join(",")
      } else if (finalKey === "enrollments") {
        // business_accounts sends compound "service:status,status" entries and needs "|"
        // between them; requests/enrollments sends plain service names and needs ","
        const isCompoundEnrollmentValue = fieldValue.some(
          (value) => typeof value === "string" && value.includes(":")
        )
        result[finalKey] = fieldValue.join(isCompoundEnrollmentValue ? "|" : ",")
      } else if (trueFalseFields.includes(finalKey)) {
        if (fieldValue.length === 1) result[finalKey] = fieldValue[0] === "Yes"
      } else if (noneFieldsMap?.[finalKey] && finalKey !== "role_status") {
        if (
          fieldValue?.some((value) =>
            ["inactive", noneFieldsMap?.[finalKey]].includes(value?.toLowerCase())
          )
        ) {
          if (fieldValue?.includes("inactive")) result[`${finalKey}_inactive`] = "inactive"
          //if(fieldValue?.includes('incomplete')) result[`${finalKey}_incomplete`] = 'incomplete'
          if (fieldValue?.map((el) => el?.toLowerCase())?.includes(noneFieldsMap?.[finalKey]))
            result[`${finalKey}_none`] = "none"

          let filteredValue = fieldValue?.filter(
            (el) => !["inactive", noneFieldsMap?.[finalKey]]?.includes(el?.toLowerCase())
          )
          if (filteredValue?.length) result[finalKey] = filteredValue.join(",")
        } else {
          result[finalKey] = fieldValue.join(",")
        }
      } else {
        result[finalKey] = fieldValue.join(",")
      }
    } else if (isNumberFilterField(finalKey)) {
      if (fieldValue.type)
        result[finalKey] =
          fieldValue.type === "is_between"
            ? `${fieldValue.min},${fieldValue.max}`
            : `${numberFilterTypeMap[fieldValue.type]} ${fieldValue.min}`
    } else if (fieldValue === false && filtersTypeById?.[finalKey]?.withNoValue) {
      // Handle "no value" filters (e.g., arn_codes, bin_codes, caid_codes set to false means "show only empty")
      result[`no_${finalKey}`] = true
    } else if (fieldValue === false && filtersTypeById?.[finalKey]?.withNoOption) {
      // Also handle "no option" filters (e.g., merchant_descriptor set to false means "show only with no descriptor").
      // When the filter declares noOptionValue, send that sentinel — the backend ignores empty/false values.
      result[`${finalKey}`] = filtersTypeById[finalKey].noOptionValue ?? false
    } else {
      if (
        (fieldValue !== undefined &&
          fieldValue !== null &&
          fieldValue !== "" &&
          fieldValue !== "all") ||
        withEmpty
      ) {
        if (dayjs.isDayjs(fieldValue)) {
          result[finalKey] = fieldValue.format(dateFormat)
        } else {
          if (fieldValue?.type === null) {
          } else {
            result[finalKey] = fieldValue || null
          }
        }
      }
    }
  }
  return result
}

export const onOkShowMessage = {
  // 'Business account created': {action:'setDemoTourStep',value: 1}
}

export const downloadFile = (url) => {
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", true)
  link.style.display = "none"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const openHideLink = (url, type) => {
  const link = document.createElement("a")
  link.setAttribute("href", url)
  if (type === "_blank") {
    link.setAttribute("target", "_blank")
  }
  link.setAttribute("rel", "noopener noreferrer")
  link.style.display = "none"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const formatDuration = (num) => {
  let m = Math.floor(num / 60)
  let s = num - m * 60
  return `${m}m ${s}s`
}

export const pinIconColor = {
  dark: "#fff",
  light: "#27272A"
}

export const debounce = (fn, delay) => {
  let timeOutId
  return function (...args) {
    if (timeOutId) {
      clearTimeout(timeOutId)
    }
    timeOutId = setTimeout(() => {
      fn(...args)
    }, delay)
  }
}
export const history = createBrowserHistory()

export const dateOptions = [
  { id: 1, name: "Today", value: "Last 0 days", days: 0 },
  { id: 2, name: "Yesterday", value: "Last 1 days", days: 1 },
  { id: 3, name: "Last 7 days", value: "Last 7 days", days: 7 },
  { id: 4, name: "Last 30 days", value: "Last 30 days", days: 30 },
  { id: 5, name: "Last 90 days", value: "Last 90 days", days: 90 },
  { id: 6, name: "Last 6 months", value: "Last 6 months", days: 180 },
  { id: 7, name: "Last 12 months", value: "Last 12 months", days: 365 }
]

export const langMapISO = {
  en: "en_US",
  de: "de_DE",
  es: "es_LA",
  fr: "fr_FR",
  it: "it_IT",
  nl: "nl_NL",
  pt: "pt_PT",
  ru: "ru_RU",
  "zh-hans": "zh-CN"
}

export function formatToTwoDecimals(num) {
  const number = parseFloat(num)

  if (number % 1 === 0) {
    return Math.round(number).toString()
  }

  const formatted = number.toFixed(2)
  if (formatted.endsWith(".00")) {
    return Math.round(number).toString()
  }

  return formatted
}

export const calendarLocale = (lang) => ({
  lang: {
    locale: langMapISO[lang],
    placeholder: "Select date",
    rangePlaceholder: ["Start date", "End date"],
    today: "Today",
    now: "Now",
    backToToday: "Back to today",
    ok: "OK",
    clear: "Clear",
    month: "Month",
    year: "Year",
    timeSelect: "Select time",
    dateSelect: "Select date",
    monthSelect: "Choose a month",
    yearSelect: "Choose a year",
    decadeSelect: "Choose a decade",
    yearFormat: "YYYY",
    dateFormat: "M/D/YYYY",
    dayFormat: "DD",
    dateTimeFormat: "M/D/YYYY HH:mm:ss",
    monthFormat: "MMMM",
    monthBeforeYear: true,
    previousMonth: "Previous month (PageUp)",
    nextMonth: "Next month (PageDown)",
    previousYear: "Last year (Control + left)",
    nextYear: "Next year (Control + right)",
    previousDecade: "Last decade",
    nextDecade: "Next decade",
    previousCentury: "Last century",
    nextCentury: "Next century"
  },
  timePickerLocale: {
    placeholder: "Select time"
  },
  dateFormat: "YYYY-MM-DD",
  dateTimeFormat: "YYYY-MM-DD HH:mm:ss",
  weekFormat: "YYYY-wo",
  monthFormat: "YYYY-MM"
})

export const getSuffix = (num) => {
  let suffix = ""

  switch (num) {
    case 1:
      suffix = "st"
      break
    case 2:
      suffix = "nd"
      break
    case 3:
      suffix = "rd"
      break
    case 4:
      suffix = "th"
      break
    default:
      suffix = "th"
      break
  }

  return suffix
}

export const removeQuery = (key, navigate) => {
  const url = new URL(window.location)
  url.searchParams.delete(key)
  navigate(`${window.location.pathname}${url.search}`, { replace: true })
}

export const parseTime = (distanceToDate) => {
  let days = Math.floor(distanceToDate / (1000 * 60 * 60 * 24))
  let hours = Math.floor((distanceToDate % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  let min = Math.floor((distanceToDate % (1000 * 60 * 60)) / (1000 * 60))
  let sec = Math.floor((distanceToDate % (1000 * 60)) / 1000)

  if (days < 10) {
    days = `0${days}`
  }

  if (hours < 10) {
    hours = `0${hours}`
  }

  if (min < 10) {
    min = `0${min}`
  }

  if (sec < 10) {
    sec = `0${sec}`
  }

  return { days, hours, min, sec }
}

export const useQuery = (search) => {
  return new URLSearchParams(search)
}

export const useBackListener = (callback) => {
  const navigator = useContext(UNSAFE_NavigationContext).navigator

  useEffect(() => {
    const listener = ({ location, action }) => {
      if (action === "POP") {
        callback({ location, action })
      }
    }

    const unlisten = navigator.listen(listener)
    return unlisten
  }, [callback, navigator])
}

export const serialize = (params) => {
  const search = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    search.append(key, value)
  }

  return search.toString()
}

// get tout-guide Element
export const getTourEl = (id) => document.querySelector(`[data-tour="${id}"]`)

export const formatCurrency = (value, rounded) => {
  const isNegative = value < 0
  return `${isNegative ? "-" : ""}$${accounting.formatNumber(
    Math.abs(value),
    rounded ? 0 : 2,
    ",",
    "."
  )}`
}

export const PARTNER_ADMIN_ID = 5

const AppUtils = {
  // Получить сообщение локализации
  getMes(context) {
    const ct = context.intl
    return function res(mesId, values) {
      try {
        return ct.formatMessage(mesId, values)
      } catch (e) {
        return mesId
      }
    }
  },

  // валидация и кориктеровка redirect uri
  controlRedirectUrl(redirectUrl, whitelist) {
    if (!redirectUrl) return ""
    // проверка по вайт листу, если он есть
    if (whitelist && whitelist.length > 0) {
      let isRedirect = false
      whitelist.forEach((item) => {
        if (redirectUrl.indexOf(item) !== -1) {
          isRedirect = true
        }
      })
      if (!isRedirect) return ""
    }

    return redirectUrl.substring(0, 4) === "http" ? redirectUrl : ""
  },

  createRequestTypes(base) {
    const REQUEST = "REQUEST"
    const SUCCESS = "SUCCESS"
    const FAILURE = "FAILURE"
    return [REQUEST, SUCCESS, FAILURE].reduce((acc, type) => {
      acc[type] = `${base}_${type}`
      return acc
    }, {})
  },

  // Перевести строку в CamelCase из underscore стиля
  toCamelCase(str, firstUpper) {
    const camelCase = str.replace(/_([a-z])/g, (m, w) => w.toUpperCase())
    if (!firstUpper) return camelCase
    return camelCase.charAt(0).toUpperCase() + camelCase.slice(1)
  },

  makeBoldText(text, type = "") {
    const splittedText = text.split("*")
    return splittedText.map((t, idx) =>
      idx % 2 ? <Text strong>{t}</Text> : <Text type={type}>{t}</Text>
    )
  },

  makeBoldTextHtml(text) {
    return text?.split("*").map((part, idx) => (idx % 2 ? <strong key={idx}>{part}</strong> : part))
  },

  toPascalCase(str) {
    if (!str) return
    let arr = str.split(" ").join("_").split("_")
    arr = arr.map((i) => this.toUpperLatter(i.toLocaleLowerCase()))
    return arr.join("")
  },

  haveCommonFieldObjects(obj1, obj2) {
    for (const key in obj1) {
      if (obj2?.hasOwnProperty(key) && obj1[key] === obj2[key]) {
        return true
      }
    }
    return false
  },

  toNormalText(str) {
    if (!str) return
    let arr = str.split("_")
    arr = arr.map((x, i) => (i === 0 ? this.toUpperLatter(x.toLocaleLowerCase()) : x))
    return arr.join(" ")
  },

  // Перевести первую букву в верхний регистр
  toUpperLatter(str) {
    if (str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    }
    return ""
  },

  toLowerLatter(str) {
    if (str) {
      return str.charAt(0).toLowerCase() + str.slice(1)
    }
    return ""
  },

  tryToBool(str) {
    if (str === "false") return false
    if (str === "true") return true
    return str
  },

  random() {
    return Math.floor(Math.random() * 0xffff)
  },

  randomKey() {
    return Math.random().toString(36).substring(2, 11)
  },

  cutUrlToPrevious(str) {
    if (!str || typeof str !== "string") return
    const index = str.lastIndexOf("/")
    return str.slice(0, index)
  },

  formatValue(val) {
    if (val == null || isNaN(val)) {
      return
    }
    val = Math.round(val)
    if (val === 100000) {
      val += "+"
    }
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  },

  copyToClipboard(str) {
    const el = document.createElement("textarea")
    el.value = str
    document.body.appendChild(el)
    el.select()
    document.execCommand("copy")
    document.body.removeChild(el)
  },

  checkMailDomain(mail) {
    if (!mail) return { type: "gmail", name: "mail", link: "google" }
    if (mail.includes("gmail")) return { type: "gmail", name: "mail", link: "google" }
    if (mail.includes("outlook")) return { type: "outlook", name: "Outlook", link: "outlook" }
    if (mail.includes("yahoo")) return { type: "yahoo", name: "Yahoo", link: "yahoo" }
    if (mail.includes("protonmail"))
      return { type: "protonmail", name: "ProtonMail", link: "protonmail" }
    if (mail.includes("aol")) return { type: "aol", name: "AOL", link: "aol" }
    if (mail.includes("zoho")) return { type: "zoho", name: "Zoho", link: "zohomail" }
    if (mail.includes("icloud")) return { type: "icloud", name: "iCloud", link: "icloud" }
    if (mail.includes("gmx")) return { type: "gmx", name: "GMX", link: "gmx" }
    if (mail.includes("hey")) return { type: "hey", name: "Hey", link: "hey" }
    if (mail.includes("yandex")) return { type: "yandex", name: "Yandex", link: "yandex" }
    return { type: "gmail", name: "Gmail", link: "google" }
  },

  convertNumber(value, fractionDigits, lowerCase = false) {
    if (value >= 1000000000)
      return `${(value / 1000000000).toFixed(fractionDigits)}${lowerCase ? "b" : "B"}`
    if (value >= 1000000)
      return `${(value / 1000000).toFixed(fractionDigits)}${lowerCase ? "m" : "M"}`
    if (value >= 1000) return `${(value / 1000).toFixed(fractionDigits)}${lowerCase ? "k" : "K"}`
    return fractionDigits >= 0 ? `${formatNumber(value, 0, ",", ".")}` : `${value}`
  },

  goLink(link, target) {
    const a = document.createElement("a")

    a.href = link
    a.target = target || "_self"
    a.click()
    return link
  },

  includeCurrentLocation(locations, pathname) {
    const splitStr = locations.split("/")
    return !!splitStr?.includes(pathname)
  },

  addDefaultSrc(event) {
    // Images.defaultImg was never a real asset, so this used to swap in a fallback that
    // 404s too, leaving the browser's own broken-image icon (e.g. a "Not specified"
    // country with no flag URL at all). Hiding the element outright is what every caller
    // actually wants here - a missing icon, not a broken one.
    event.target.style.display = "none"
  },

  randomNum(min, max) {
    return Math.floor(Math.random() * (max - min)) + min
  },

  range(start, end, step = 1) {
    const output = []
    if (typeof end === "undefined") {
      end = start
      start = 0
    }
    for (let i = start; i < end; i += step) {
      output.push(i)
    }
    return output
  },

  validateEmail(email) {
    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
  },

  includeSearch(value1, value2) {
    return value1.toLowerCase().indexOf(value2.replace(/\+/g, "").toLowerCase()) >= 0
  },
  starsReplace(value, companyDetails = false) {
    if (value && !companyDetails) {
      return value.length > 12 ? "************" : "*".repeat(value.length)
    }
    return "*******"
  },

  getHour() {
    const date = new Date()
    return date.getHours()
  },

  getSearchParamsByName(searchParams, name) {
    return (
      searchParams
        ?.split("&")
        ?.filter((el) => el?.includes(name))?.[0]
        ?.split("=")?.[1] || ""
    )
  },

  changeImageSize(url, size) {
    if (!url) return ""
    return url?.replace(/(\.jpg\?)|(\.png\?)|(\.gif\?)/, function (match) {
      return `_${size}x${match}`
    })
  },

  durationAsString(start, end) {
    const duration = dayjs.duration(dayjs.utc(start).diff(dayjs.utc(end)))

    //Get Days
    const days = dayjs.utc(start).diff(dayjs.utc(end), "days")
    const daysFormatted = days ? `${days} ${days === 1 ? "Day" : "Days"}` : "" // if no full days then do not display it at all

    //Get Hours
    const hours = duration.hours()
    const hoursFormatted =
      !daysFormatted && !hours ? "" : `${hours} ${hours === 1 ? "Hour" : "Hours"}`

    //Get Minutes
    const minutes = duration.minutes()
    const minutesFormatted =
      !daysFormatted && !hours && !minutes
        ? ""
        : `${minutes} ${minutes === 1 ? "Minute" : "Minutes"}`

    const endText = minutesFormatted ? "ago" : "just now"

    return [daysFormatted, hoursFormatted, minutesFormatted, endText].join(" ")
  },

  multiplyPriceType: {
    monthly: 1,
    yearly: 12
  }
}

const mobileHeaderTitleMap = [
  { match: (p) => p.startsWith("/settings"), title: "Settings" },
  { match: (p) => p.startsWith("/dashboard"), title: "Dashboard" },
  { match: (p) => p.startsWith("/alerts"), title: "Alerts" },
  { match: (p) => p.startsWith("/credits"), title: "Credits" },
  { match: (p) => p.startsWith("/configure"), title: "Configure" },
  { match: (p) => p.startsWith("/team"), title: "Members" },
  { match: (p) => p.startsWith("/integrations"), title: "Integrations" },
  { match: (p) => p.startsWith("/developers"), title: "Developers" },
  { match: (p) => p.startsWith("/health"), title: "Health" },
  { match: (p) => p.startsWith("/getting_started"), title: "Getting started" }
]

export const getMobileHeaderTitle = (pathname) => {
  const entry = mobileHeaderTitleMap.find(({ match }) => match(pathname))
  return entry ? entry.title : ""
}

export default AppUtils
